import { useEffect } from 'react';

/**
 * Measures the exact pixel width of text rendered in a specific font size and family (Google SERP).
 * Uses Canvas 2D measureText in the browser, with an accurate character-metric fallback.
 */
function measureTextPx(text, fontSize = 20, fontFamily = 'Arial, sans-serif') {
  if (!text) return 0;
  if (typeof document !== 'undefined') {
    try {
      if (!measureTextPx._canvas) {
        measureTextPx._canvas = document.createElement('canvas');
      }
      const ctx = measureTextPx._canvas.getContext('2d');
      if (ctx) {
        ctx.font = `${fontSize}px ${fontFamily}`;
        return ctx.measureText(text).width;
      }
    } catch {
      // Fallback below
    }
  }

  // Fallback estimation for Arial
  let width = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/[ijlI1.,'|:;!]/.test(ch)) width += fontSize * 0.28;
    else if (/[frtJ -]/.test(ch)) width += fontSize * 0.35;
    else if (/[abcdeghknopquvxyz0-9]/.test(ch)) width += fontSize * 0.55;
    else if (/[ABCEGHNOPTVXYZ]/.test(ch)) width += fontSize * 0.65;
    else if (/[mwMDQKUR]/.test(ch)) width += fontSize * 0.78;
    else if (/[MW@%]/.test(ch)) width += fontSize * 0.95;
    else width += fontSize * 0.55;
  }
  return width;
}

/**
 * Clamps text to a maximum pixel width in the given font without cutting words awkwardly.
 */
export function clampToPixels(text, maxPx, fontSize = 20, fontFamily = 'Arial, sans-serif') {
  if (!text) return '';
  const currentPx = measureTextPx(text, fontSize, fontFamily);
  if (currentPx <= maxPx) return text;

  const words = text.split(' ');
  let result = '';

  for (let i = 0; i < words.length; i++) {
    const candidate = result ? `${result} ${words[i]}` : words[i];
    const candidateWithEllipsis = `${candidate}...`;
    if (measureTextPx(candidateWithEllipsis, fontSize, fontFamily) <= maxPx) {
      result = candidate;
    } else {
      break;
    }
  }

  return result ? `${result}...` : text.slice(0, Math.floor(maxPx / (fontSize * 0.6))) + '...';
}

/**
 * SEOHead ensures metadata is set on the document and strictly guarantees:
 * - Title pixel width <= 512px (SERP 20px Arial)
 * - Description pixel width <= 970px (SERP 14px Arial)
 */
export default function SEOHead({ title, description, ogImage }) {
  useEffect(() => {
    // Determine raw title
    let rawTitle = title || 'Djurs Massage & Zoneterapi | Ryomgård';

    // If title doesn't mention brand, add suffix ONLY if total pixels <= 512px
    if (!rawTitle.includes('Djurs Massage')) {
      const withSuffix = `${rawTitle} | Djurs Massage`;
      if (measureTextPx(withSuffix, 20, 'Arial, sans-serif') <= 512) {
        rawTitle = withSuffix;
      }
    }

    // Strictly enforce max 512px for title (SERP title limit)
    const finalTitle = clampToPixels(rawTitle, 512, 20, 'Arial, sans-serif');
    document.title = finalTitle;

    // Determine description
    const rawDesc =
      description ||
      'Professionel fysiurgisk massage, RAB zoneterapi og smertelindring i Ryomgård. Mærk forskellen med over 24 års erfaring. Book din tid online hos Djurs Massage.';

    // Strictly enforce max 970px for description (SERP description limit)
    const finalDesc = clampToPixels(rawDesc, 970, 14, 'Arial, sans-serif');

    // Helper to get or create a tag
    const setMetaTag = (selector, createAttrs, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.content = content;
    };

    // Standard Meta
    setMetaTag('meta[name="description"]', { name: 'description' }, finalDesc);

    // Open Graph
    setMetaTag('meta[property="og:title"]', { property: 'og:title' }, finalTitle);
    setMetaTag('meta[property="og:description"]', { property: 'og:description' }, finalDesc);
    const finalOgImage =
      ogImage ||
      'https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-klinik-ryomgaard.jpg';
    setMetaTag('meta[property="og:image"]', { property: 'og:image' }, finalOgImage);
    setMetaTag('meta[property="og:type"]', { property: 'og:type' }, 'website');

    // Twitter Cards
    setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, finalTitle);
    setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, finalDesc);
    setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, finalOgImage);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;
  }, [title, description, ogImage]);

  return null;
}
